import React from 'react';
import { Container, Typography, Paper, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';

export default function FullIntegration1Docs() {
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Full Integration - Part 1
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/')}>
          🏠 HOME
        </Button>
      </Box>

      <Typography variant="body1" sx={{ mb: 3 }}>
        React Hook Form, MUI, GraphQL을 완전히 통합한 창고 관리 시스템의 첫 번째 단계입니다. 
        프로젝트 구조 설계부터 기본 설정까지, 실무에서 사용할 수 있는 확장 가능한 아키텍처를 구축해보겠습니다.
      </Typography>

      <Typography variant="h4" gutterBottom sx={{ mt: 4, mb: 2 }}>
        프로젝트 구조와 기본 설정
      </Typography>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        완성된 프로젝트 구조
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        확장 가능하고 유지보수가 용이한 폴더 구조입니다. 기능별로 명확하게 분리되어 있어 팀 개발에 적합합니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`warehouse-management/
├── src/
│   ├── components/
│   │   ├── forms/
│   │   │   ├── ProductForm.tsx
│   │   │   ├── CategoryForm.tsx
│   │   │   └── InventoryForm.tsx
│   │   ├── tables/
│   │   │   ├── ProductTable.tsx
│   │   │   ├── InventoryTable.tsx
│   │   │   └── DataGridCustom.tsx
│   │   ├── layout/
│   │   │   ├── AppLayout.tsx
│   │   │   ├── Sidebar.tsx
│   │   │   └── Header.tsx
│   │   └── charts/
│   │       ├── SalesChart.tsx
│   │       └── InventoryChart.tsx
│   ├── pages/
│   │   ├── ProductsPage.tsx
│   │   ├── InventoryPage.tsx
│   │   ├── DashboardPage.tsx
│   │   └── ReportsPage.tsx
│   ├── graphql/
│   │   ├── queries/
│   │   ├── mutations/
│   │   ├── subscriptions/
│   │   └── fragments/
│   ├── hooks/
│   │   ├── useProducts.ts
│   │   ├── useInventory.ts
│   │   └── useRealtime.ts
│   ├── types/
│   │   └── index.ts
│   └── utils/
│       ├── validation.ts
│       └── formatting.ts`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        통합 설정 파일
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Apollo Client에서 HTTP와 WebSocket을 모두 지원하는 설정입니다. 실시간 구독과 캐싱 정책을 함께 구성합니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`// src/apollo/client.ts
import { 
  ApolloClient, 
  InMemoryCache, 
  createHttpLink, 
  split 
} from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';

const httpLink = createHttpLink({
  uri: 'http://localhost:4000/graphql',
});

const wsLink = new GraphQLWsLink(
  createClient({
    url: 'ws://localhost:4000/graphql',
  })
);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  httpLink,
);

const client = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache({
    typePolicies: {
      Product: {
        fields: {
          inventory: {
            merge(existing, incoming) {
              return incoming;
            }
          }
        }
      }
    }
  }),
});

export default client;`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        타입 정의
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        TypeScript 타입 정의로 개발 시 타입 안전성을 보장합니다. GraphQL 스키마와 일치하도록 정의했습니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`// src/types/index.ts
export interface Product {
  id: string;
  name: string;
  description?: string;
  price: number;
  category: Category;
  inventory: Inventory;
  images: string[];
  createdAt: string;
  updatedAt: string;
}

export interface Category {
  id: string;
  name: string;
  description?: string;
  products: Product[];
}

export interface Inventory {
  id: string;
  productId: string;
  quantity: number;
  minStock: number;
  maxStock: number;
  reorderPoint: number;
  lastUpdated: string;
}

export interface ProductInput {
  name: string;
  description?: string;
  price: number;
  categoryId: string;
  images?: string[];
}

export interface InventoryInput {
  productId: string;
  quantity: number;
  minStock: number;
  maxStock: number;
  reorderPoint: number;
}

export interface FormErrors {
  [key: string]: string | undefined;
}`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        GraphQL 스키마 정의
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`// GraphQL 스키마 (서버측)
type Product {
  id: ID!
  name: String!
  description: String
  price: Float!
  category: Category!
  inventory: Inventory!
  images: [String!]!
  createdAt: String!
  updatedAt: String!
}

type Category {
  id: ID!
  name: String!
  description: String
  products: [Product!]!
}

type Inventory {
  id: ID!
  productId: String!
  quantity: Int!
  minStock: Int!
  maxStock: Int!
  reorderPoint: Int!
  lastUpdated: String!
}

type Query {
  products(filter: ProductFilter, pagination: PaginationInput): ProductConnection!
  product(id: ID!): Product
  categories: [Category!]!
  inventory(productId: ID): [Inventory!]!
  lowStockProducts: [Product!]!
  dashboardStats: DashboardStats!
}

type Mutation {
  createProduct(input: ProductInput!): Product!
  updateProduct(id: ID!, input: ProductInput!): Product!
  deleteProduct(id: ID!): Boolean!
  
  updateInventory(input: InventoryInput!): Inventory!
  bulkUpdateInventory(inputs: [InventoryInput!]!): [Inventory!]!
}

type Subscription {
  productUpdated: Product!
  inventoryUpdated: Inventory!
  lowStockAlert: Product!
}`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        환경 설정 및 의존성
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`// package.json 주요 의존성
{
  "dependencies": {
    "@apollo/client": "^3.8.0",
    "@mui/material": "^5.14.0",
    "@mui/x-data-grid": "^6.10.0",
    "@mui/x-date-pickers": "^6.10.0",
    "react": "^18.2.0",
    "react-hook-form": "^7.45.0",
    "react-router-dom": "^6.14.0",
    "recharts": "^2.7.0",
    "graphql": "^16.7.0",
    "yup": "^1.2.0"
  }
}

// .env 파일
REACT_APP_GRAPHQL_HTTP_URI=http://localhost:4000/graphql
REACT_APP_GRAPHQL_WS_URI=ws://localhost:4000/graphql
REACT_APP_API_KEY=your_api_key_here`}
        </Typography>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/')}
        >
          🏠 BACK TO HOME
        </Button>
        <Button 
          variant="contained" 
          onClick={() => navigate('/docs/full-integration-2')}
        >
          PART 2: 실제 컴포넌트 구현 →
        </Button>
      </Box>
    </Container>
  );
}